import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";
import { api } from "@/lib/site";

export default function BlogPreview() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="blog" data-testid="blog-section" className="py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-white/30" />
          <span className="label-eyebrow">05 — Insights</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-12 items-end">
          <h2 className="md:col-span-7 font-display font-bold text-3xl sm:text-4xl md:text-5xl tracking-tight text-white">
            Field notes from the front lines <br />
            <span className="text-slate-500">of cloud and security.</span>
          </h2>
          <p className="md:col-span-5 text-slate-400 leading-relaxed">
            Practical perspectives I've taken into Fortune-500 boardrooms,
            classrooms and SOCs.
          </p>
        </div>

        {loading ? (
          <div className="text-slate-500 text-sm">Loading posts…</div>
        ) : posts.length === 0 ? (
          <div className="text-slate-500 text-sm">No posts yet — check back soon.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-[#0A0C13] border border-white/10 rounded-2xl overflow-hidden hover:border-white/25 hover:-translate-y-1 transition-all duration-300"
              >
                <Link to={`/blog/${p.slug}`}>
                  {p.cover_image && (
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={p.cover_image}
                        alt={p.title}
                        className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C13] via-transparent to-transparent" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      <Calendar size={12} />
                      {new Date(p.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </div>
                    <h3 className="mt-3 font-display font-semibold text-lg md:text-xl text-white tracking-tight leading-snug">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-sm text-slate-400 line-clamp-3">{p.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-sm text-blue-300 group-hover:text-blue-200">
                      Read insight <ArrowUpRight size={14} />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
